import React, { useState, useEffect } from "react";
import { useEyeconxStore } from "../../../../store/eyeconxStore";
import settingsService from "../../../../services/settings.service";
import Loading from "../../../common/Loading";

const FeatureCard = () => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [clinicFeatures, setClinicFeatures] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (clinicId) {
            fetchClinicFeatures();
        }
    }, [clinicId]);

    const fetchClinicFeatures = async () => {
        try {
            setIsLoading(true);
            const res = await settingsService.getClinicFeatures(clinicId);
            setClinicFeatures(res.data || {});
        } catch (error) {
            console.error("Failed to fetch clinic features:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const featureCategories = [
        {
            title: "Communication Features",
            icon: "bi-chat-dots",
            features: [
                { name: "Two-Way Texting", description: "Enable bidirectional text communication", enabled: clinicFeatures?.twoWayText, key: "twoWayText" },
                { name: "Anti-Spam Compliance", description: "Comply with anti-spam regulations", enabled: clinicFeatures?.isAntispamLaw, key: "isAntispamLaw" }
            ]
        },
        {
            title: "Reminder Features",
            icon: "bi-bell",
            features: [
                { name: "Email Reminders", description: "Send reminders via email", enabled: clinicFeatures?.reminderEmail, key: "reminderEmail" },
                { name: "SMS Reminders", description: "Send reminders via text message", enabled: clinicFeatures?.reminderSms, key: "reminderSms" },
                { name: "Call Reminders", description: "Send reminders via phone call", enabled: clinicFeatures?.reminderCall, key: "reminderCall" }
            ]
        },
        {
            title: "Newsletter Features",
            icon: "bi-envelope-paper",
            features: [
                { name: "Email Newsletter", description: "Send newsletters via email", enabled: clinicFeatures?.newsletterEmail, key: "newsletterEmail" },
                { name: "SMS Newsletter", description: "Send newsletters via text message", enabled: clinicFeatures?.newsletterSms, key: "newsletterSms" }
            ]
        },
        {
            title: "Marketing Features",
            icon: "bi-megaphone",
            features: [
                { name: "Email Blast", description: "Send marketing emails", enabled: clinicFeatures?.blastEmail, key: "blastEmail" },
                { name: "SMS Blast", description: "Send marketing text messages", enabled: clinicFeatures?.blastSms, key: "blastSms" },
                { name: "Call Blast", description: "Send marketing phone calls", enabled: clinicFeatures?.blastCall, key: "blastCall" }
            ]
        },
        {
            title: "Patient Engagement Features",
            icon: "bi-people",
            features: [
                { name: "Online Booking", description: "Allow patients to book appointments online", enabled: clinicFeatures?.booking, key: "booking" },
                { name: "Patient Recall", description: "Automated patient recall system", enabled: clinicFeatures?.recall, key: "recall" },
                { name: "Survey Reminders", description: "Send reminders to complete surveys", enabled: clinicFeatures?.surveyReminder, key: "surveyReminder" }
            ]
        },
        {
            title: "Reputation & Feedback Features",
            icon: "bi-star",
            features: [
                { name: "Reputation Management", description: "Monitor and manage online reputation", enabled: clinicFeatures?.reputation, key: "reputation" },
                { name: "Patient Surveys", description: "Collect and analyze patient feedback", enabled: clinicFeatures?.survey, key: "survey" }
            ]
        }
    ];

    const FeatureItem = ({ feature }) => (
        <div className="feature-item">
            <div className={`feature-icon ${feature.enabled ? "feature-enabled" : "feature-disabled"}`}>
                <i className={`bi ${feature.enabled ? "bi-check-lg" : "bi-x-lg"}`}></i>
            </div>
            <div>
                <h6 className="mb-1">{feature.name}</h6>
                <p className="mb-0 text-muted small">{feature.description}</p>
            </div>
        </div>
    );

    const FeatureCategory = ({ category }) => (
        <div className="feature-category">
            <h5 className="fw-bold mb-3">
                <i className={`bi ${category.icon} me-2`}></i>
                {category.title}
            </h5>
            <div className="row">
                {category.features.map((feature) => (
                    <div key={feature.key} className="col-md-6 col-lg-4">
                        <FeatureItem feature={feature} />
                    </div>
                ))}
            </div>
        </div>
    );

    if (isLoading) return <Loading />;

    return (
        <>


            <div className="row justify-content-center">
                <div className="col-lg-12">
                    <div className="card bg-light  shadow-sm border-0 h-100">
                        <div className="card-body p-4">
                            {featureCategories.map((category, index) => (
                                <FeatureCategory key={index} category={category} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .card {
                    border: none;
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.05);
                    border-radius: 12px;
                    overflow: hidden;
                }
                .feature-category {
                    border-left: 4px solid #6c5ce7;
                    padding-left: 1rem;
                    margin-bottom: 1.5rem;
                }
                .feature-item {
                    display: flex;
                    align-items: center;
                    margin-bottom: 0.75rem;
                    padding: 0.75rem;
                    border-radius: 8px;
                    transition: background-color 0.2s;
                }
                .feature-item:hover {
                    background-color: #f8f9fa;
                }
                .feature-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 1rem;
                    flex-shrink: 0;
                }
                .feature-enabled {
                    background-color: #e8f5e9;
                    color: #2e7d32;
                }
                .feature-disabled {
                    background-color: #ffebee;
                    color: #c62828;
                }
            `}</style>
        </>
    );
};

export default FeatureCard;
